import * as d3 from 'd3';
import cloneDeep from 'lodash/cloneDeep';
import diff from 'lodash/difference';
import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import size from 'lodash/size';
import values from 'lodash/values';

import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import TreeData, { StartNode } from 'src/presentation/components/pages/ivr/components/ivrTree/models/TreeData';
import generateAlphaNumericId from 'src/presentation/components/pages/ivr/helpers/generateAlphaNumericId';
import {
  NodeSize,
  OPTION_NODE_TYPE,
  RECT_MARGIN,
  START_NODE_ID,
  TreeLayoutNodeTypeEnum,
} from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';

import TreeNode from 'src/presentation/components/pages/ivr/components/ivrTree/models/TreeNode';
import { IvrConfig } from 'src/presentation/components/pages/ivr/components/ivrTree/config/ivrConfig';
import { Point } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import { calculateNodePosition } from './nodesPositionHelper';
import { generateLinks } from './linksHelper';
import { getNodesRect } from './nodesSettingsHelper';
import { getTimeRangeForTemporarySplitNode } from 'src/presentation/components/pages/ivr/components/helpers/temporarySplitNodeHelpers';
import { isTemporarySplitNode } from 'src/presentation/components/pages/ivr/helpers/ivrHelpers';

const DEFAULT_NEXT_ID_FIELD = 'ivrNode.defaultNextId';
export const ERROR_TEXT = 'Error';
export const DEFAULT_TEXT = 'Default to';
export const MATCH_TEXT = 'Match';
export const NO_MATCH_TEXT = 'No match';
export enum INTERNAL_NODE_TYPES {
  HIDDEN = 'HIDDEN',
  START = 'START',
}

const NODES_WITH_OPTIONS = [
  NODE_TYPES.IVR_NODE_MENU,
  NODE_TYPES.IVR_NODE_NUMBER_MATCH,
  NODE_TYPES.IVR_NODE_VARIABLE_MATCH,
  NODE_TYPES.IVR_NODE_SCHEDULE,
];

type OptionNode = {
  children?: any[];
  id: string;
  isNotMatch?: boolean;
  isError?: boolean;
  name: string;
  nextId: string;
  ref?: string;
  type: string;
};

let treeNodesMapById: any = {};

const getTreeNodeName = node => {
  if ([NODE_TYPES.IVR_NODE_DIAL, NODE_TYPES.IVR_NODE_VOICEMAIL].includes(node.type)) {
    return node.ivrNode.selection || node.ivrNode.description || node.ivrNode.name;
  }
  return node.ivrNode.name;
};

const getAbandonedNodes = (ivrConfig: IvrConfig) => {
  const ids = diff(Object.keys(ivrConfig.nodesMap), Object.keys(treeNodesMapById));

  const nodes = ids
    .map(id => ivrConfig.findById(id))
    .sort(node => {
      return NODES_WITH_OPTIONS.includes(node.type) ? -1 : 0;
    });

  return nodes;
};

const handleAbandonedNodes = (ivrConfig: IvrConfig, offset: Point) => {
  const nds = getAbandonedNodes(ivrConfig);

  const x = offset[0] + RECT_MARGIN;
  const y = offset[1] + RECT_MARGIN;

  const m = map(nds, (node, index) => {
    const nd = {
      ...node,
      height: NodeSize.Default.height,
      id: node.ivrNode.id,
      name: getTreeNodeName(node),
      width: NodeSize.Default.width,
      x,
      y: y + (NodeSize.Default.height + 16) * index,
    };

    const treeNode: any = new TreeNode(nd);
    return treeNode;
  });

  return m;
};

const getNodesPositionsFromTreeLayout = ({ treeLayout, unlinkedNodes }) => {
  return reduce(
    [...treeLayout.descendants(), ...unlinkedNodes],
    (acc, node) => {
      acc[node.data ? node.data.id : node.id] = { x: node.x, y: node.y };
      return acc;
    },
    {}
  );
};

export const calculateNodesPositionsForTemplate = ({ locationState, parentId, templateConfig }) => {
  const startNodeExists = !!find(locationState, (_, nodeId) => nodeId === START_NODE_ID);
  const noStartNode = startNodeExists && size(locationState) > 1;

  const [templateNodesLocations] = calculateNodesPositions({
    ivrConfig: templateConfig,
    noStartNode,
  });

  const rect = getNodesRect(map(templateNodesLocations, location => location));
  const nodePosition = calculateNodePosition({
    parentId,
    locationState: locationState || {},
    width: rect.width,
    height: rect.height,
  });

  for (const node in templateNodesLocations) {
    templateNodesLocations[node].x += nodePosition.x + Math.abs(rect.x0);
    templateNodesLocations[node].y += nodePosition.y + Math.abs(rect.y0);
  }

  return templateNodesLocations;
};

export const calculateNodesPositions = ({ ivrConfig, noStartNode = false }) => {
  const startNode: any = createIvrNode(START_NODE_ID, 'start', { ivrNode: { id: '-d' } });
  startNode.type = INTERNAL_NODE_TYPES.START;
  startNode.hideLinks = true;

  let root;
  if (ivrConfig.rootNode) {
    const name = getTreeNodeName(ivrConfig.rootNode);

    root = createIvrNode(ivrConfig.rootNode!.ivrNode.id, name, ivrConfig.rootNode);
    root.children = handleNodeChildren(ivrConfig.rootNode, ivrConfig, root, root);

    startNode.children.push(root);
  }

  const treeNodesHierarchy = d3.hierarchy(noStartNode ? root : startNode, node =>
    node.data ? node.data.children : node.children
  );

  const tree: any = d3.tree().nodeSize([NodeSize.Default.width + 160, NodeSize.Default.height + 60]);
  const treeLayout = tree(treeNodesHierarchy);
  const rect = getNodesRect(treeLayout.descendants());
  const unlinkedNodes = handleAbandonedNodes(ivrConfig, [rect.x0 - 2 * NodeSize.Default.width, 0]);

  const nodesLocations = getNodesPositionsFromTreeLayout({ treeLayout, unlinkedNodes });
  const treeNodes = treeLayout.descendants();
  preparationNodes(treeNodes);
  return [nodesLocations, treeNodes, unlinkedNodes];
};

const getNodesWithPositions = ({ ivrConfig, locationState }) => {
  const locationState1 = cloneDeep(locationState);
  return map([START_NODE_ID, ...Object.keys(ivrConfig.nodesMap)], (key: any) => {
    const isStart = key === START_NODE_ID;
    const node = isStart ? StartNode : ivrConfig.nodesMap[key];
    let position = locationState1[key];

    if (!position) {
      const parentId = ivrConfig.getParentId(node.ivrNode.id);
      position = calculateNodePosition({ parentId, locationState: locationState1 });
      locationState1[node.ivrNode.id] = position;
    }

    const width = isStart ? NodeSize.StartNode.width : NodeSize.Default.width;
    const height = isStart ? NodeSize.StartNode.height : NodeSize.Default.height;

    const result = {
      ...node,
      ...position,
      height,
      id: node.id || node.ivrNode.id,
      name: node.name || node.ivrNode.name,
      nodeType: key === START_NODE_ID ? TreeLayoutNodeTypeEnum.START : TreeLayoutNodeTypeEnum.NODE,
      width,
    };

    return result;
  }).filter(node => !isNil(node.x));
};

export const transform = (ivrConfig: IvrConfig, locationState: any): [TreeData, any] => {
  treeNodesMapById = {};

  let treeNodes: any[] = [];

  const ivrTreeNodes = getNodesWithPositions({ ivrConfig, locationState }) || [];
  preparationNodes(ivrTreeNodes);
  treeNodes = map(ivrTreeNodes, node => new TreeNode(node));

  const rootNode = treeNodes.find(node => node.id === ivrConfig.rootNodeId);

  const transformedData: any = new TreeData(treeNodes, ivrConfig.nodesHierarchy, rootNode);
  const links = generateLinks(treeNodes, ivrConfig);
  transformedData.links = links;

  const rect = transformedData.getRect();

  const width = rect.x1 - rect.x0;
  const height = rect.y1 - rect.y0;
  const scale = 50;

  const ivrFrameSize = {
    width: scale * width,
    height: scale * height,
    marginLeft: -rect.x0 + width * (scale / 2),
    marginTop: -rect.y0 + height * (scale / 2),
    leftTopPoint: [rect.x0 - width * (scale / 2), rect.y0 - height * (scale / 2)],
    rightBottomPoint: [rect.x1 + width * (scale / 2), rect.y1 + height * (scale / 2)],
    unlinkedNodesPosition: [rect.x0 - 1.5 * NodeSize.Default.width, rect.y0 + 20],
  };

  return [transformedData, ivrFrameSize];
};

const adjustNode = (node: any) => {
  if (!node.nodeType) {
    node.nodeType = get(node, 'data.type') === 'START' ? TreeLayoutNodeTypeEnum.START : TreeLayoutNodeTypeEnum.NODE;
    node.name = node.data.name;
    node.id = node.data.id;
    node.type = get(node, 'data.node.type');
  }

  switch (node.nodeType) {
    case TreeLayoutNodeTypeEnum.NODE:
      node.width = NodeSize.Default.width;
      node.height = NodeSize.Default.height;

      if (node.name.length > 1) {
        node.height += 5;
      }

      break;
    case TreeLayoutNodeTypeEnum.START:
      node.width = NodeSize.StartNode.width;
      node.height = NodeSize.StartNode.height;

      node.y = node.y + (NodeSize.Default.height - NodeSize.StartNode.height) / 2;
      node.x = node.x + (NodeSize.Default.width - NodeSize.StartNode.width) / 2;

      break;
  }
  return node;
};

const preparationNodes = (ivrTreeNodes: any) => {
  each(ivrTreeNodes, (node: any) => {
    return adjustNode(node);
  });
};

const createIvrNode = (id: string, name: string, node: any, children: any[] = []) => {
  const newNode = {
    id,
    name,
    node,
    children,
  };
  if (newNode && !has(treeNodesMapById, id)) {
    treeNodesMapById[id] = newNode;
  }

  return newNode;
};

const getOptionId = (parentNodeId, index) => `${parentNodeId}-${index}`.replace(' ', '-');

const createIvrOptionNode = ({
  name,
  nextId,
  option,
  index,
  parentNodeId,
}: {
  name: string;
  nextId: string;
  option?: string;
  index: string;
  parentNodeId: string;
}) => {
  const id = parentNodeId ? getOptionId(parentNodeId, index) : generateAlphaNumericId();
  const nd = {
    id,
    name,
    nextId,
    type: OPTION_NODE_TYPE,
    option,
    optionIndex: index,
  };

  return nd;
};

const getNextNode = (nextId: string, conf: IvrConfig) => {
  if (has(treeNodesMapById, nextId)) {
    return [true, treeNodesMapById[nextId]];
  }

  const childNode = conf.findById(nextId);
  if (childNode) {
    treeNodesMapById[nextId] = childNode;
  }
  return [false, childNode];
};

const handleNodeChildren = (parentNode: any, conf: IvrConfig, root: any, parentTreeNode: any) => {
  let children;

  switch (parentNode.type as NODE_TYPES) {
    case NODE_TYPES.IVR_NODE_MENU:
    case NODE_TYPES.IVR_NODE_NUMBER_MATCH:
    case NODE_TYPES.IVR_NODE_SCHEDULE:
    case NODE_TYPES.IVR_NODE_VARIABLE_MATCH:
      children = handleOptionNode(parentNode, conf, root);
      break;
    case NODE_TYPES.IVR_NODE_MESSAGE:
    case NODE_TYPES.IVR_NODE_DIAL:
    default:
      const child = handleDefaultNode(parentNode, conf, root, parentTreeNode);
      children = Array.isArray(child) ? child : child ? [child] : undefined;
      break;
  }
  return children ? children.filter(child => !!child) : undefined;
};

const createErrorNode = (parentNodeId: string, errorNodeId: string, conf: IvrConfig, root: any) => {
  const notMatchNode: OptionNode = createIvrOptionNode({
    name: ERROR_TEXT,
    nextId: errorNodeId,
    index: ERROR_TEXT,
    parentNodeId,
  });
  notMatchNode.isError = true;
  const notMatchNodeChild = createNode(notMatchNode, notMatchNode.nextId, conf, root);
  if (notMatchNodeChild) {
    notMatchNode.children = [notMatchNodeChild];
  }
  return notMatchNodeChild;
};

const handleDefaultNode = (parentNode: any, conf: IvrConfig, root: any, parentTreeNode: any) => {
  const { nextId, errorNodeId } = parentNode.ivrNode;

  const node = createNode(parentTreeNode, nextId, conf, root);
  if (errorNodeId) {
    const errorNode = createErrorNode(parentNode.ivrNode.id, errorNodeId, conf, root);
    return [node, ...(errorNode ? [errorNode] : [])];
  }

  return node;
};

const NODE_OPTIONS_MAPPER = {
  [NODE_TYPES.IVR_NODE_MENU]: {
    create: (option: any, index: string, parentNodeId) =>
      createIvrOptionNode({
        name: `Press ${option.dtmf}`,
        nextId: option.nextId,
        option: option.dtmf,
        index,
        parentNodeId,
      }),
    notMatchedIdField: 'ivrNode.badSelectionNodeId',
    optionsListField: 'ivrNode.menu',
    noMatchBadSelectionNodeName: ERROR_TEXT,
  },
  [NODE_TYPES.IVR_NODE_NUMBER_MATCH]: {
    create: (option: any, index: string, parentNodeId) =>
      createIvrOptionNode({ name: MATCH_TEXT, nextId: option.nextId, option: option.pattern, index, parentNodeId }),
    notMatchedIdField: DEFAULT_NEXT_ID_FIELD,
    optionsListField: 'ivrNode.numberMatchers',
    noMatchBadSelectionNodeName: NO_MATCH_TEXT,
  },
  [NODE_TYPES.IVR_NODE_VARIABLE_MATCH]: {
    create: (option: any, index: string, parentNodeId) =>
      createIvrOptionNode({ name: MATCH_TEXT, nextId: option.nextId, option: option.pattern, index, parentNodeId }),
    notMatchedIdField: DEFAULT_NEXT_ID_FIELD,
    optionsListField: 'ivrNode.variableMatches',
    noMatchBadSelectionNodeName: NO_MATCH_TEXT,
  },
  [NODE_TYPES.IVR_NODE_SCHEDULE]: {
    create: (option: any, index: string, parentNodeId) =>
      createIvrOptionNode({
        name: isTemporarySplitNode(parentNodeId) ? 'On' : MATCH_TEXT,
        nextId: option.nodeId,
        option: option.schedule,
        index,
        parentNodeId,
      }),
    notMatchedIdField: DEFAULT_NEXT_ID_FIELD,
    optionsListField: 'ivrNode.nodeSchedules',
    noMatchBadSelectionNodeName: NO_MATCH_TEXT,
    optionNextIdField: 'nodeId',
  },
};

const handleOptionNode = (parentNode: any, conf: IvrConfig, root: any) => {
  const optionNodeData = NODE_OPTIONS_MAPPER[parentNode.type];
  const nextIdFieldName = optionNodeData.optionNextIdField || 'nextId';

  const notMatchId = get(parentNode, optionNodeData.notMatchedIdField);
  const optionsList = filter(
    get(parentNode, optionNodeData.optionsListField),
    option => !!get(option, nextIdFieldName)
  );
  const errorNodeId = get(parentNode, 'ivrNode.errorNodeId');
  const defaultOffId = get(parentNode, 'ivrNode.defaultOffId');
  const distinctNextNodesMap = reduce(
    optionsList,
    (acc, node) => {
      const nextId = get(node, nextIdFieldName);
      if (nextId && !has(acc, nextId)) {
        acc[nextId] = node;
      }
      return acc;
    },
    {}
  );

  let distinctNextNodes = parentNode.type === NODE_TYPES.IVR_NODE_MENU ? optionsList : values(distinctNextNodesMap);

  const parentNodeId = parentNode.ivrNode.id;
  let notMatchNodeName = optionNodeData.noMatchBadSelectionNodeName;

  if (parentNode.type === NODE_TYPES.IVR_NODE_SCHEDULE && isTemporarySplitNode(parentNodeId)) {
    const { isActive } = getTimeRangeForTemporarySplitNode(get(parentNode, 'ivrNode.nodeSchedules', []));
    distinctNextNodes = isActive ? distinctNextNodes : [];
    notMatchNodeName = 'Off';
  }

  let children = map(distinctNextNodes, (option: any, index: number) => {
    const optionNode: OptionNode = optionNodeData.create(option, index, parentNodeId);
    const optionNodeChild = createNode(optionNode, optionNode.nextId, conf, root);
    if (optionNodeChild) {
      optionNode.children = [optionNodeChild];
    }
    const nextNodeExists = has(treeNodesMapById, optionNode.nextId);
    return nextNodeExists ? optionNodeChild : undefined;
  });
  let errorNode;
  if (errorNodeId) {
    errorNode = createErrorNode(parentNode.ivrNode.id, errorNodeId, conf, root);
  }
  let defaultOffIdNode;
  if (defaultOffId) {
    defaultOffIdNode = createNode(parentNode, defaultOffId, conf, root);
  }

  if (notMatchId) {
    const notMatchNode: OptionNode = createIvrOptionNode({
      name: notMatchNodeName,
      nextId: notMatchId,
      index: optionNodeData.noMatchBadSelectionNodeName,
      parentNodeId: parentNode.ivrNode.id,
    });
    const notMatchNodeChild = createNode(notMatchNode, notMatchNode.nextId, conf, root);
    notMatchNode.isNotMatch = true;
    if (notMatchNodeChild) {
      notMatchNode.children = [notMatchNodeChild];
    }
    const optionNextNodeExists = notMatchNodeChild || (notMatchNode.ref && treeNodesMapById[notMatchNode.ref]);
    children = [...children, ...(optionNextNodeExists ? [notMatchNodeChild] : [])];
  }
  return [...children, ...(errorNode ? [errorNode] : []), ...(defaultOffIdNode ? [defaultOffIdNode] : [])];
};

const createNode = (
  parentTreeNode: any,
  nextId: any,
  conf: any,
  root: any,
  nameFn = (node: any) => getTreeNodeName(node)
) => {
  if (!nextId) {
    return;
  }
  const [existInTree, node] = getNextNode(nextId, conf);
  if (!node) {
    return;
  }

  if (existInTree) {
    return undefined;
  } else {
    if (node && has(treeNodesMapById, nextId)) {
      treeNodesMapById[nextId] = node;
    }
  }
  const resultNode: any = createIvrNode(node.ivrNode.id, nameFn(node), node);

  const children: any = handleNodeChildren(node, conf, root, resultNode);
  if (Array.isArray(children)) {
    resultNode.children = children;
  }
  return resultNode;
};
